<template>
  <BaseModal
    :model-value="modelValue"
    class="gap-0 !rounded-2xl w-full max-w-[420px] max-sm:top-3 max-sm:translate-y-0 max-sm:max-w-[calc(100vw-30px)] max-h-[calc(100dvh-24px)] p-0 overflow-x-hidden overflow-y-auto"
    @update:model-value="handleClose"
  >
    <div class="relative flex flex-col items-center w-full">
      <div
        class="relative w-full h-[190px] sm:h-[250px] overflow-hidden"
      >
        <InlineSvg
          :src="shardsBonusFill"
          aria-hidden="true"
          class="absolute top-0 left-0 w-full h-auto pointer-events-none brightness-125"
          unique-ids
        />
        <InlineSvg
          :src="shardsBonusTexture"
          aria-hidden="true"
          class="absolute top-0 left-0 w-full h-auto pointer-events-none brightness-125"
          unique-ids
        />
        <img
          :src="shardsBonusImage"
          :alt="t('flashSale.badge')"
          class="absolute top-0 left-0 w-full h-auto"
        >
        <InlineSvg
          :src="shardsBonusStroke"
          aria-hidden="true"
          class="absolute top-0 left-0 w-full h-auto pointer-events-none"
          unique-ids
        />
      </div>

      <div class="relative z-10 -mt-4 sm:-mt-3 mb-3">
        <div class="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-[#00C8FF]/25 bg-[#0a0a1f]/80 backdrop-blur-sm">
          <span class="text-[#00C8FF] text-sm font-semibold">{{ t('flashSale.badge') }}</span>
          <span class="text-purple-gray text-sm">{{ t('flashSale.badgeSuffix') }}</span>
        </div>
      </div>

      <div class="relative z-10 w-full px-4 sm:px-5 pb-4 sm:pb-5 flex flex-col items-center">
        <h2 class="text-lavender text-[24px] sm:text-[32px] leading-7 sm:leading-9 tracking-[-1.4px] text-center mb-2">
          {{ t('flashSale.title', { percent: selectedOption.bonusPercent }) }}
        </h2>

        <p class="text-purple-gray text-[13px] sm:text-[15px] text-center leading-5 mb-4 max-w-[360px]">
          {{ t('flashSale.description') }}
        </p>

        <div class="grid grid-cols-3 gap-2 w-full mb-4">
          <button
            v-for="(option, index) in options"
            :key="option.id"
            type="button"
            class="relative rounded-2xl min-h-[100px] sm:min-h-[112px] py-3 px-1.5 sm:px-2 transition-all cursor-pointer text-center overflow-hidden"
            :class="selectedOptionId === option.id
              ? 'border border-[#00C8FF]/60 bg-[#00C8FF]/[0.05]'
              : 'border border-white/10 bg-white/[0.02] hover:border-white/20'
            "
            @click="selectedOptionId = option.id"
          >
            <template
              v-for="side in glowSides(index)"
              :key="side"
            >
              <div
                class="pointer-events-none absolute bottom-0 w-[96px] h-[56px] rounded-full bg-[#5858F5] opacity-70 blur-[36px]"
                :class="side === 'right' ? '-right-4' : '-left-4'"
              />
              <div
                class="pointer-events-none absolute bottom-0 w-[56px] h-[32px] rounded-full bg-[#00C8FF] opacity-80 blur-[24px] mix-blend-plus-lighter"
                :class="side === 'right' ? 'right-0' : 'left-0'"
              />
              <div
                class="pointer-events-none absolute bottom-0 w-[36px] h-[20px] rounded-full bg-[#00C8FF] opacity-90 blur-[14px] mix-blend-plus-lighter"
                :class="side === 'right' ? 'right-[4px]' : 'left-[4px]'"
              />
              <div
                class="pointer-events-none absolute bottom-0 w-[20px] h-[8px] rounded-full bg-white opacity-90 blur-[6px] mix-blend-plus-lighter"
                :class="side === 'right' ? 'right-[8px]' : 'left-[8px]'"
              />
            </template>

            <div
              class="relative z-10 text-base sm:text-lg font-semibold tracking-[-0.5px] mb-2 leading-5"
              :class="selectedOptionId === option.id ? 'text-lavender' : 'text-purple-gray'"
            >
              {{ option.usdAmount ? t('flashSale.optionAmount', { amount: option.usdAmount }) : t('flashSale.optionAnyAmount') }}
              <span class="block mt-1 font-normal text-xs leading-4">
                {{ t('flashSale.optionDurationYear') }}
              </span>
            </div>
            <div
              class="relative z-10 text-[12px] font-bold uppercase tracking-[0.5px] leading-4"
              :class="selectedOptionId === option.id ? 'text-[#00C8FF]' : 'text-[#00C8FF]/50'"
            >
              {{ t('flashSale.optionBonus', { percent: option.bonusPercent }) }}
            </div>
          </button>
        </div>

        <!-- CTA Button -->
        <GlowButton
          :color="EButtonColor.BLUE"
          class="w-full"
          @click="handleClaim"
        >
          <div class="flex items-center gap-2">
            <img
              :src="shardsIcon"
              alt=""
              aria-hidden="true"
              class="size-5 shrink-0"
            >
            <span>{{ t('flashSale.ctaPrefix') }}</span>
            <span class="text-[#00C8FF] font-bold">{{ selectedOption.bonusPercent }}%</span>
            <span>{{ t('flashSale.ctaSuffix') }}</span>
            <span class="ml-0.5">→</span>
          </div>
        </GlowButton>

        <div class="mt-3 flex flex-col items-center gap-2 w-full">
          <span class="text-purple-gray text-sm">{{ t('flashSale.endsIn') }}</span>
          <div class="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 max-w-full">
            <div
              v-if="timeRemaining.days > 0"
              class="flex items-center justify-center min-w-[44px] sm:min-w-[52px] h-9 sm:h-10 px-2 sm:px-3 rounded-xl bg-white/[0.04] border border-white/[0.07]"
            >
              <span class="text-lavender text-sm sm:text-base font-semibold tabular-nums">{{ timeRemaining.days }}{{ t('flashSale.dayShort') }}</span>
            </div>
            <div class="flex items-center justify-center min-w-[44px] sm:min-w-[52px] h-9 sm:h-10 px-2 sm:px-3 rounded-xl bg-white/[0.04] border border-white/[0.07]">
              <span class="text-lavender text-sm sm:text-base font-semibold tabular-nums">{{ padTwo(timeRemaining.hours) }}{{ t('flashSale.hourShort') }}</span>
            </div>
            <div class="flex items-center justify-center min-w-[44px] sm:min-w-[52px] h-9 sm:h-10 px-2 sm:px-3 rounded-xl bg-white/[0.04] border border-white/[0.07]">
              <span class="text-lavender text-sm sm:text-base font-semibold tabular-nums">{{ padTwo(timeRemaining.minutes) }}{{ t('flashSale.minuteShort') }}</span>
            </div>
            <div class="flex items-center justify-center min-w-[44px] sm:min-w-[52px] h-9 sm:h-10 px-2 sm:px-3 rounded-xl bg-white/[0.04] border border-white/[0.07]">
              <span class="text-lavender text-sm sm:text-base font-semibold tabular-nums">{{ padTwo(timeRemaining.seconds) }}{{ t('flashSale.secondShort') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import InlineSvg from 'vue-inline-svg'
import mixpanel from 'mixpanel-browser'
import BaseModal from '@/components/ui/dialog/BaseModal.vue'
import GlowButton from '@/components/ui/button/GlowButton.vue'
import { EButtonColor } from '@/types/shared/button'
import { useTranslation } from '@/composables/use-i18n'
import { useFlashSaleModal } from '@/composables/use-flash-sale-modal'
import { useLingoPrice } from '@/composables/contracts/lingo-price'
import { LingoRouteName } from '@/router/routes'
import { LOCK_DURATION_ID } from '@/types/staking'
import shardsBonusFill from '@/assets/images/shards-bonus/shards-bonus-fill.svg'
import shardsBonusTexture from '@/assets/images/shards-bonus/shards-bonus-texture.svg'
import shardsBonusStroke from '@/assets/images/shards-bonus/shards-bonus-strok.svg'
import shardsBonusImage from '@/assets/images/shards-bonus/shards-bonus.svg'
import shardsIcon from '@/assets/icons/shards.svg'

const { t } = useTranslation()
const router = useRouter()
const { timeRemaining, dismissForToday } = useFlashSaleModal()
const { price } = useLingoPrice()

defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

interface FlashSaleOption {
  id: string
  usdAmount?: number
  bonusPercent: number
  lockDuration: LOCK_DURATION_ID
}

const options: FlashSaleOption[] = [
  {
    id: 'any-12mo',
    bonusPercent: 15,
    lockDuration: LOCK_DURATION_ID.TWELVE_MONTHS,
  },
  {
    id: '500-12mo',
    usdAmount: 500,
    bonusPercent: 25,
    lockDuration: LOCK_DURATION_ID.TWELVE_MONTHS,
  },
  {
    id: '1000-12mo',
    usdAmount: 1000,
    bonusPercent: 40,
    lockDuration: LOCK_DURATION_ID.TWELVE_MONTHS,
  },
]

const selectedOptionId = ref('1000-12mo')

const selectedOption = computed(() =>
  options.find(o => o.id === selectedOptionId.value) ?? options[1],
)

const glowSides = (index: number) => {
  if (index === 1) return ['left', 'right']
  return index === 0 ? ['right'] : ['left']
}

const padTwo = (n: number) => String(n).padStart(2, '0')

const handleClose = (val: boolean) => {
  if (!val) {
    dismissForToday()
  }
  emit('update:modelValue', val)
}

const handleClaim = () => {
  const option = selectedOption.value
  const lingoAmount = option.usdAmount && price.value > 0
    ? Math.ceil(option.usdAmount / price.value)
    : null

  mixpanel.track('Flash Sale Modal CTA Clicked', {
    tier: option.id,
    usdAmount: option.usdAmount ?? null,
    bonusPercent: option.bonusPercent,
  })

  dismissForToday()
  emit('update:modelValue', false)

  const query: Record<string, string> = {
    presetDuration: option.lockDuration,
    entryPoint: 'flashSale',
  }
  if (lingoAmount) {
    query.presetAmount = String(lingoAmount)
  }

  router.push({
    name: LingoRouteName.STAKING_NEW,
    query,
  })
}
</script>
